import { FC } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";

import "./ScheduleInfo.scss";
import { DateTimeFormat, formatDate, getDurationInWords } from "helpers/dateTimeHelper";
import { FreightModeBaseTypes } from "../../../../../../../constants";
import {
  formatQuotedTransitTime,
  getFlightScheduleDateTime,
  getStopsCodes,
  getUniqueStops,
  OceanVesselLeg,
  VesselScheduleSummary,
} from "../../scheduleUtils";

type ScheduleInfoProps = {
  transitStops: any;
  freightModeType: typeof FreightModeBaseTypes[keyof typeof FreightModeBaseTypes];
  vesselScheduleSummary?: VesselScheduleSummary | null;
  oceanVesselLegs: OceanVesselLeg[];
};

const ScheduleInfo: FC<ScheduleInfoProps> = function (props) {
  const { freightModeType, transitStops, vesselScheduleSummary, oceanVesselLegs } = props;
  const intl = useIntl();
  const isAirFreight = freightModeType === "air";
  const isOceanFreight = freightModeType === "ocean";

  const dateNotProvidedMsg = intl.formatMessage({
    id: "tracking.dateNotProvided",
    defaultMessage: "Date not provided",
  });

  if (!transitStops?.length) {
    return null;
  }

  /** Air freight: first / last transit row are flight endpoints; middle rows are unique airports. */
  if (isAirFreight) {
    const initialDeparture = transitStops[0];
    const finalArrival = transitStops.at(-1);
    const stops = getUniqueStops(transitStops.slice(1, transitStops.length - 1));

    const initialDepartureDateTime = getFlightScheduleDateTime(true, initialDeparture);
    const finalArrivalDateTime = getFlightScheduleDateTime(true, finalArrival);

    const initialDepartureCode = initialDeparture.airport_code;
    const finalArrivalCode = finalArrival.airport_code;

    return (
      <div className="schedule-info">
        <div className="d-flex flex-column schedule-info__left-section">
          <div className="text-secondary text-sm font-medium">
            <FormattedMessage id="general.departure" defaultMessage="Departure" />
          </div>
          <div className="text-primary text-md font-medium">
            {initialDepartureDateTime.isValid()
              ? formatDate(initialDepartureDateTime, DateTimeFormat.dateFormat)
              : dateNotProvidedMsg}
          </div>
          <div className="text-tertiary text-md">{initialDepartureCode || ""}</div>
        </div>
        <div className="schedule-info__stop-container">
          <div className="text-xs text-secondary" style={{ minHeight: "18px" }}>
            {getDurationInWords(initialDepartureDateTime, finalArrivalDateTime)}
          </div>
          <div className="w-100 position-relative my-lg">
            <div className="schedule-info__stops">
              {stops?.length > 0 &&
                stops.map((stop: any) => (
                  <OverlayTrigger
                    key={stop.stop_id}
                    placement="top"
                    overlay={<Tooltip id={String(stop.stop_id)}>{stop.airport}</Tooltip>}
                  >
                    <div className="schedule-info__stops-item" />
                  </OverlayTrigger>
                ))}
            </div>
          </div>
          {stops?.length > 0 && (
            <>
              <div className="text-xs text-brand-secondary">
                {stops.length} stop{stops.length > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-tertiary">{getStopsCodes(true, stops)}</div>
            </>
          )}
        </div>
        <div className="d-flex flex-column schedule-info__right-section">
          <div className="text-secondary text-sm font-medium">
            <FormattedMessage id="general.arrival" defaultMessage="Arrival" />
          </div>
          <div className="text-primary text-md font-medium">
            {finalArrivalDateTime.isValid()
              ? formatDate(finalArrivalDateTime, DateTimeFormat.dateFormat)
              : dateNotProvidedMsg}
          </div>
          <div className="text-tertiary text-md">{finalArrivalCode || ""}</div>
        </div>
      </div>
    );
  }

  /** Ocean: endpoints from first departure / last arrival seaport; timeline dots = vessel legs. */
  if (isOceanFreight) {
    const firstDepartureStop = transitStops.find((s: any) => s.type === "departure_seaport");
    const reversedTransitStops = [...transitStops].reverse();
    const lastArrivalStop = reversedTransitStops.find((s: any) => s.type === "arrival_seaport");
    const firstEndpoint = firstDepartureStop ?? transitStops.find((s: any) => s.type === "arrival_seaport");
    const lastEndpoint = lastArrivalStop ?? reversedTransitStops.find((s: any) => s.type === "departure_seaport");

    const initialDepartureDateTime = firstEndpoint
      ? getFlightScheduleDateTime(false, firstEndpoint)
      : getFlightScheduleDateTime(false, transitStops[0]);
    const finalArrivalDateTime = lastEndpoint
      ? getFlightScheduleDateTime(false, lastEndpoint)
      : getFlightScheduleDateTime(false, transitStops.at(-1));

    const initialDepartureCode = firstEndpoint?.port_locode ?? "";
    const finalArrivalCode = lastEndpoint?.port_locode ?? "";

    const quotedDuration = formatQuotedTransitTime(vesselScheduleSummary?.transit_time);
    const computedDuration =
      initialDepartureDateTime.isValid() && finalArrivalDateTime.isValid()
        ? getDurationInWords(initialDepartureDateTime, finalArrivalDateTime)
        : null;
    const durationLabel = quotedDuration ?? computedDuration ?? "";

    const summaryCount = Math.max(
      vesselScheduleSummary?.quoted_vessel_count ?? 0,
      vesselScheduleSummary?.current_vessel_count ?? 0,
    );
    let vesselDotCount = Math.max(summaryCount, oceanVesselLegs.length);
    if (vesselDotCount === 0 && oceanVesselLegs.length === 1) {
      vesselDotCount = 1;
    }

    const displayVesselCount = Math.max(summaryCount, oceanVesselLegs.length);
    const useCompactOceanTimeline = displayVesselCount >= 5;

    const oceanTooltip = (leg: OceanVesselLeg | undefined, index: number) => {
      if (!leg) {
        return intl.formatMessage(
          { id: "tracking.vesselScheduleLegTitle", defaultMessage: "Vessel {number}" },
          { number: index + 1 },
        );
      }
      const dep = leg.departure?.port_name || leg.departure?.port_locode || "";
      const arr = leg.arrival?.port_name || leg.arrival?.port_locode || "";
      if (dep && arr) return `${dep} → ${arr}`;
      return dep || arr || `Vessel ${index + 1}`;
    };

    return (
      <div className="schedule-info schedule-info--ocean">
        <div className="d-flex flex-column schedule-info__left-section">
          <div className="text-secondary text-sm font-medium">
            <FormattedMessage id="general.departure" defaultMessage="Departure" />
          </div>
          <div className="text-primary text-md font-medium">
            {initialDepartureDateTime.isValid()
              ? formatDate(initialDepartureDateTime, DateTimeFormat.dateFormat)
              : dateNotProvidedMsg}
          </div>
          <div className="text-tertiary text-md">{initialDepartureCode}</div>
        </div>
        <div className="schedule-info__stop-container">
          <div className="text-xs text-secondary schedule-info__duration" style={{ minHeight: "18px" }}>
            {durationLabel}
          </div>
          <div className="w-100 position-relative my-lg">
            <div
              className={`schedule-info__stops schedule-info__stops--ocean${
                useCompactOceanTimeline ? " schedule-info__stops--ocean-compact" : ""
              }`}
            >
              {vesselDotCount > 0 &&
                (useCompactOceanTimeline ? (
                  <>
                    <div className="schedule-info__stops-segment schedule-info__stops-segment--start" />
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="ocean-tip-0">{oceanTooltip(oceanVesselLegs[0], 0)}</Tooltip>}
                    >
                      <div className="schedule-info__stops-item" />
                    </OverlayTrigger>
                    <div className="schedule-info__stops-segment schedule-info__stops-segment--middle" />
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`ocean-tip-${vesselDotCount - 1}`}>
                          {oceanTooltip(oceanVesselLegs[vesselDotCount - 1], vesselDotCount - 1)}
                        </Tooltip>
                      }
                    >
                      <div className="schedule-info__stops-item" />
                    </OverlayTrigger>
                    <div className="schedule-info__stops-segment schedule-info__stops-segment--end" />
                  </>
                ) : (
                  Array.from({ length: vesselDotCount }).map((_, index) => (
                    <OverlayTrigger
                      key={`ocean-dot-${oceanVesselLegs[index]?.stop_id ?? index}`}
                      placement="top"
                      overlay={<Tooltip id={`ocean-tip-${index}`}>{oceanTooltip(oceanVesselLegs[index], index)}</Tooltip>}
                    >
                      <div className="schedule-info__stops-item" />
                    </OverlayTrigger>
                  ))
                ))}
            </div>
          </div>
          {displayVesselCount > 0 && (
            <div className="text-xs text-brand-secondary schedule-info__vessel-count">
              {intl.formatMessage(
                {
                  id: "tracking.vesselScheduleVesselCount",
                  defaultMessage: "{count, plural, one {# vessel} other {# vessels}}",
                },
                { count: displayVesselCount },
              )}
            </div>
          )}
        </div>
        <div className="d-flex flex-column schedule-info__right-section">
          <div className="text-secondary text-sm font-medium">
            <FormattedMessage id="general.arrival" defaultMessage="Arrival" />
          </div>
          <div className="text-primary text-md font-medium">
            {finalArrivalDateTime.isValid()
              ? formatDate(finalArrivalDateTime, DateTimeFormat.dateFormat)
              : dateNotProvidedMsg}
          </div>
          <div className="text-tertiary text-md">{finalArrivalCode}</div>
        </div>
      </div>
    );
  }

  /** Other modes: first / last transit stop (legacy behavior). */
  const initialDeparture = transitStops[0];
  const finalArrival = transitStops.at(-1);
  const stops = getUniqueStops(transitStops.slice(1, transitStops.length - 1));

  const initialDepartureDateTime = getFlightScheduleDateTime(false, initialDeparture);
  const finalArrivalDateTime = getFlightScheduleDateTime(false, finalArrival);

  const initialDepartureCode = initialDeparture.port_locode ?? "";
  const finalArrivalCode = finalArrival.port_locode ?? "";

  return (
    <div className="schedule-info">
      <div className="d-flex flex-column schedule-info__left-section">
        <div className="text-secondary text-sm font-medium">
          <FormattedMessage id="general.departure" defaultMessage="Departure" />
        </div>
        <div className="text-primary text-md font-medium">
          {initialDepartureDateTime.isValid()
            ? formatDate(initialDepartureDateTime, DateTimeFormat.dateFormat)
            : dateNotProvidedMsg}
        </div>
        <div className="text-tertiary text-md">{initialDepartureCode || ""}</div>
      </div>
      <div className="schedule-info__stop-container">
        <div className="text-xs text-secondary" style={{ minHeight: "18px" }}>
          {getDurationInWords(initialDepartureDateTime, finalArrivalDateTime)}
        </div>
        <div className="w-100 position-relative my-lg">
          <div className="schedule-info__stops">
            {stops?.length > 0 &&
              stops.map((stop: any) => (
                <OverlayTrigger
                  key={stop.stop_id}
                  placement="top"
                  overlay={<Tooltip id={String(stop.stop_id)}>{stop.airport}</Tooltip>}
                >
                  <div className="schedule-info__stops-item" />
                </OverlayTrigger>
              ))}
          </div>
        </div>
        {stops?.length > 0 && (
          <>
            <div className="text-xs text-brand-secondary">
              {stops.length} stop{stops.length > 1 ? "s" : ""}
            </div>
            <div className="text-xs text-tertiary">{getStopsCodes(false, stops)}</div>
          </>
        )}
      </div>
      <div className="d-flex flex-column schedule-info__right-section">
        <div className="text-secondary text-sm font-medium">
          <FormattedMessage id="general.arrival" defaultMessage="Arrival" />
        </div>
        <div className="text-primary text-md font-medium">
          {finalArrivalDateTime.isValid() ? formatDate(finalArrivalDateTime, DateTimeFormat.dateFormat) : dateNotProvidedMsg}
        </div>
        <div className="text-tertiary text-md">{finalArrivalCode || ""}</div>
      </div>
    </div>
  );
};

export default ScheduleInfo;
